// Backend runtime classification for a game (Cycle-5/6 seam, D-089).
//
// A game's *user-facing* multiplayer intent is the `multiplayer` taxonomy field
// (single | local_coop | online_realtime | online_turn_based). Its *backend
// runtime* — how the game-dev runner builds + runs it — is a separate, platform
// concern derived from that intent + a feature gate:
//
//   - `static-html`        → a single self-contained `game.html` (the realized
//                            path; local_coop is still one HTML file, two players
//                            share a keyboard). Served straight from S3.
//   - `multiplayer-server` → a server-authoritative runtime (Node ws server + a
//                            thin browser client), the shape proven by the
//                            zombie-multiplayer prior-art. Needs the runner to
//                            build + run server code, not only static HTML.
//
// This module is PURE (no IO, no FEATURES import) so it is unit-testable and the
// caller decides the gate. The actual server build/run path is a later slice
// (cycle 6); this is only the classification seam it plugs into.

import { z } from 'zod';
import { MultiplayerModel } from './schemas/game.js';

/** How the game-dev runner builds + runs a game. */
export const GameRuntime = z.enum(['static-html', 'multiplayer-server']);
export type GameRuntime = z.infer<typeof GameRuntime>;

/** Online multiplayer models that warrant a server-backed runtime. */
const SERVER_BACKED: ReadonlySet<MultiplayerModel> = new Set<MultiplayerModel>([
  'online_realtime',
  'online_turn_based',
]);

/**
 * Classify a game's backend runtime from its multiplayer taxonomy + the
 * `multiplayerRuntime` feature gate. Pure.
 *
 * Returns `multiplayer-server` ONLY when the gate is on AND the model is an
 * online (networked) one; everything else — including `local_coop` and any game
 * while the gate is off — is `static-html`. So flipping the gate off makes every
 * game fall back to the realized static path with no other change.
 */
export function deriveGameRuntime(
  multiplayer: MultiplayerModel | undefined,
  opts: { multiplayerEnabled: boolean },
): GameRuntime {
  if (opts.multiplayerEnabled && multiplayer && SERVER_BACKED.has(multiplayer)) {
    return 'multiplayer-server';
  }
  return 'static-html';
}
